import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SortUtil } from '@iot-platform/iot-platform-utils';
import { FavoriteView, Filter } from '@iot-platform/models/common';
import { I4BGrid, I4BGridData, I4BGridOptions } from '@iot-platform/models/grid-engine';
import { Store } from '@ngrx/store';
import { FavoriteViewsActions } from '../actions';
import * as fromFavoriteViews from '../reducers';

@Injectable({
  providedIn: 'root'
})
export class FavoriteViewsFacade {
  selectedSitesFavoriteView = computed(() => {
    const selectedId = this.selectedSitesFavoriteViewId();
    const all = this.entities();
    return selectedId && all[selectedId];
  });
  selectedAssetsFavoriteView = computed(() => {
    const selectedId = this.selectedAssetsFavoriteViewId();
    const all = this.entities();
    return selectedId && all[selectedId];
  });
  selectedDevicesFavoriteView = computed(() => {
    const selectedId = this.selectedDevicesFavoriteViewId();
    const all = this.entities();
    return selectedId && all[selectedId];
  });
  selectedAssetEventsFavoriteView = computed(() => {
    const selectedId = this.selectedAssetEventsFavoriteViewId();
    const all = this.entities();
    return selectedId && all[selectedId];
  });
  selectedDeviceEventsFavoriteView = computed(() => {
    const selectedId = this.selectedDeviceEventsFavoriteViewId();
    const all = this.entities();
    return selectedId && all[selectedId];
  });
  selectSitesFavoriteViewsConfiguration = computed(() => {
    const favoriteViews = this.sitesFavoriteViews();
    const currentFavoriteView = this.selectedSitesFavoriteView();
    const isFavoriteViewsLoading = this.loading();
    return {
      sortedFavoriteViews: favoriteViews.sort(SortUtil.sortByName),
      currentFavoriteView,
      isFavoriteViewsLoading
    };
  });
  selectedAssetsFavoriteViewsConfiguration = computed(() => {
    const favoriteViews = this.assetsFavoriteViews();
    const currentFavoriteView = this.selectedAssetsFavoriteView();
    const isFavoriteViewsLoading = this.loading();
    return {
      sortedFavoriteViews: favoriteViews.sort(SortUtil.sortByName),
      currentFavoriteView,
      isFavoriteViewsLoading
    };
  });
  selectedDevicesFavoriteViewsConfiguration = computed(() => {
    const favoriteViews = this.devicesFavoriteViews();
    const currentFavoriteView = this.selectedDevicesFavoriteView();
    const isFavoriteViewsLoading = this.loading();
    return {
      sortedFavoriteViews: favoriteViews.sort(SortUtil.sortByName),
      currentFavoriteView,
      isFavoriteViewsLoading
    };
  });
  selectedAssetEventsFavoriteViewsConfiguration = computed(() => {
    const favoriteViews = this.assetEventsFavoriteViews();
    const currentFavoriteView = this.selectedAssetEventsFavoriteView();
    const isFavoriteViewsLoading = this.loading();
    return {
      sortedFavoriteViews: favoriteViews.sort(SortUtil.sortByName),
      currentFavoriteView,
      isFavoriteViewsLoading
    };
  });
  selectedDeviceEventsFavoriteViewsConfiguration = computed(() => {
    const favoriteViews = this.deviceEventsFavoriteViews();
    const currentFavoriteView = this.selectedDeviceEventsFavoriteView();
    const isFavoriteViewsLoading = this.loading();
    return {
      sortedFavoriteViews: favoriteViews.sort(SortUtil.sortByName),
      currentFavoriteView,
      isFavoriteViewsLoading
    };
  });
  private readonly store: Store = inject(Store);
  all = this.store.selectSignal(fromFavoriteViews.getAllFavoriteViews);
  entities = this.store.selectSignal(fromFavoriteViews.getFavoriteViewsEntities);
  sharedFavoriteViews = this.store.selectSignal(fromFavoriteViews.getSharedFavoriteViews);
  notSharedFavoriteViews = this.store.selectSignal(fromFavoriteViews.getNotSharedFavoriteViews);
  loading = this.store.selectSignal(fromFavoriteViews.getLoading);
  loaded = this.store.selectSignal(fromFavoriteViews.getLoaded);
  sitesFavoriteViews = this.store.selectSignal(fromFavoriteViews.getFavoriteViewsForMasterViewSites);
  assetsFavoriteViews = this.store.selectSignal(fromFavoriteViews.getFavoriteViewsForMasterViewAssets);
  devicesFavoriteViews = this.store.selectSignal(fromFavoriteViews.getFavoriteViewsForMasterViewDevices);
  assetEventsFavoriteViews = this.store.selectSignal(fromFavoriteViews.getFavoriteViewsForMasterViewAssetEvents);
  deviceEventsFavoriteViews = this.store.selectSignal(fromFavoriteViews.getFavoriteViewsForMasterViewDeviceEvents);
  selectedSitesFavoriteViewId = this.store.selectSignal(fromFavoriteViews.getSelectedFavoriteViewIdForMasterViewSites);
  selectedAssetsFavoriteViewId = this.store.selectSignal(fromFavoriteViews.getSelectedFavoriteViewIdForMasterViewAssets);
  selectedDevicesFavoriteViewId = this.store.selectSignal(fromFavoriteViews.getSelectedFavoriteViewIdForMasterViewDevices);
  selectedAssetEventsFavoriteViewId = this.store.selectSignal(fromFavoriteViews.getSelectedFavoriteViewIdForMasterViewAssetEvents);
  selectedDeviceEventsFavoriteViewId = this.store.selectSignal(fromFavoriteViews.getSelectedFavoriteViewIdForMasterViewDeviceEvents);
  selectedFiltersForSites = this.store.selectSignal(fromFavoriteViews.getFiltersForMasterViewSites);
  selectedFiltersForAssets = this.store.selectSignal(fromFavoriteViews.getFiltersForMasterViewAssets);
  selectedFiltersForDevices = this.store.selectSignal(fromFavoriteViews.getFiltersForMasterViewDevices);
  selectedFiltersForAssetEvents = this.store.selectSignal(fromFavoriteViews.getFiltersForMasterViewAssetEvents);
  selectedFiltersForDeviceEvents = this.store.selectSignal(fromFavoriteViews.getFiltersForMasterViewDeviceEvents);

  sitesFavoriteViews$ = toObservable(this.sitesFavoriteViews);
  assetsFavoriteViews$ = toObservable(this.assetsFavoriteViews);
  devicesFavoriteViews$ = toObservable(this.devicesFavoriteViews);
  assetEventsFavoriteViews$ = toObservable(this.assetEventsFavoriteViews);
  deviceEventsFavoriteViews$ = toObservable(this.deviceEventsFavoriteViews);
  selectedFiltersForSites$ = toObservable(this.selectedFiltersForSites);
  selectedFiltersForAssets$ = toObservable(this.selectedFiltersForAssets);
  selectedFiltersForDevices$ = toObservable(this.selectedFiltersForDevices);
  selectedFiltersForAssetEvents$ = toObservable(this.selectedFiltersForAssetEvents);
  selectedFiltersForDeviceEvents$ = toObservable(this.selectedFiltersForDeviceEvents);

  loadFavoriteViews(): void {
    this.store.dispatch(FavoriteViewsActions.loadFavoriteViews());
  }

  addFavoriteView(favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.addFavoriteView({ favoriteView }));
  }

  addFavoriteViewFromMV(favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.addFavoriteViewFromMV({ favoriteView }));
  }

  updateFavoriteView(favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.updateFavoriteView({ favoriteView }));
  }

  updateFavoriteViewFromMV(favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.updateFavoriteViewFromMV({ favoriteView }));
  }

  deleteFavoriteView(favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.deleteFavoriteView({ favoriteView }));
  }

  duplicateFavoriteView(favoriteViewToDuplicate: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.duplicateFavoriteView({ favoriteViewToDuplicate }));
  }

  shareGridThenAddFavoriteView(grid: I4BGrid<I4BGridOptions, I4BGridData>, favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.shareGridThenAddFavoriteView({ grid, favoriteView }));
  }

  shareGridThenAddFavoriteViewFromMV(grid: I4BGrid<I4BGridOptions, I4BGridData>, favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.shareGridThenAddFavoriteViewFromMV({ grid, favoriteView }));
  }

  shareGridThenUpdateFavoriteView(grid: I4BGrid<I4BGridOptions, I4BGridData>, favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.shareGridThenUpdateFavoriteView({ grid, favoriteView }));
  }

  shareGridThenUpdateFavoriteViewFromMV(grid: I4BGrid<I4BGridOptions, I4BGridData>, favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.shareGridThenUpdateFavoriteViewFromMV({ grid, favoriteView }));
  }

  loadCountForFavoriteView(favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.getCountForFavoriteView({ favoriteView }));
  }

  setCurrentFavoriteView(masterView: string, favoriteView: FavoriteView): void {
    this.store.dispatch(FavoriteViewsActions.setCurrentFavoriteView({ masterView, favoriteView }));
  }

  setCurrentFilters(masterView: string, filters: Filter[]): void {
    this.store.dispatch(FavoriteViewsActions.setCurrentFilters({ masterView, filters }));
  }

  resetState(): void {
    this.store.dispatch(FavoriteViewsActions.resetState());
  }
}
